/**
 * @fileoverview Searchable combobox for selecting a canonical or custom field key.
 *
 * Groups options by category from FIELD_CATEGORIES plus any org-level custom
 * field definitions. Used in the visual PDF field editor to assign a key to
 * each placed field.
 */

import { useCallback, useRef, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import {
  FIELD_CATEGORIES,
  resolveCategoryLabel,
  resolveFieldLabel,
} from "../../../../convex/settings/precedents/canonicalFields";
import {
  isBankruptcyAppointmentType,
  isPersonalInsolvencyAppointmentType,
} from "@cvx/lib/appointmentTypes";
import {
  buildContactCustomFieldKey,
  buildContactFieldKey,
  buildContactSourceFieldKey,
  contactFieldPartsForDomain,
  CONTACT_SOURCE_FIELDS,
  CONTACT_SOURCE_KIND_LABELS,
  type ContactSourceKind,
} from "../../../../convex/jobs/precedents/contactFields";
import { FIRM_CATEGORY_META, ROLE_TO_SOURCE_KINDS } from "@/lib/contact-roles";
import type { AppointmentType } from "@cvx/jobs/schema";
import type { ContactRole, FirmContactCategory } from "@/lib/contact-roles";
import {
  FIELD_SOURCE_STYLES,
  FIELD_SOURCE_ORDER,
  type FieldSource,
} from "@/utils/precedents/classifyFieldSource";

export interface CustomFieldDef {
  _id: string;
  categoryId: string;
  categoryLabel: string;
  key: string;
  label: string;
  fieldType: string;
  /** Set when the field is scoped to a single template (document-only). */
  templateId?: string;
}

/**
 * An org-defined firm-contact custom field, scoped to one firm category.
 * Surfaces in the picker as `Firm Contact (Category) — Label` when the
 * template is tagged with that category.
 */
export interface FirmContactCustomFieldOption {
  category: FirmContactCategory;
  key: string;
  label: string;
}

interface CanonicalKeyPickerProps {
  value: string;
  onChange: (key: string) => void;
  className?: string;
  /** Org-level custom field definitions to show alongside canonical fields */
  customFields?: CustomFieldDef[];
  /**
   * Contact roles / firm categories this template fans out over. When
   * provided, the picker offers `contact:*` keys so a PDF form field can
   * be mapped directly to a Job Contact / Firm Contact part.
   */
  contactTags?: {
    jobRoles: ContactRole[];
    firmCategories: FirmContactCategory[];
  };
  /**
   * Org-defined firm-contact custom fields. Those whose category is tagged
   * on the template are offered as `contact_firm_cf_*` keys alongside the
   * basic Firm Contact parts.
   */
  firmContactCustomFields?: FirmContactCustomFieldOption[];
  /**
   * Appointment types the parent template is tagged with. When non-empty,
   * the picker hides canonical fields whose `appointmentTypes` don't
   * overlap — e.g. a template tagged only `["court_liquidation"]` won't
   * surface `bankrupt_name` or `act_of_bankruptcy_date`. Advisory only:
   * the user can toggle "Show all fields" to opt out. Fields without an
   * applicability list always appear. See MOR-429.
   */
  appointmentTypes?: AppointmentType[];
  /**
   * Classify a field key into its source bucket — colours each key chip
   * and powers the source-filter tabs. Optional; without it the picker
   * shows plain amber chips and no filter row.
   */
  classifySource?: (key: string) => FieldSource | null;
}

function PickerItem({
  field,
  isSelected,
  source,
  onSelect,
}: {
  field: { key: string; label: string };
  isSelected: boolean;
  source: FieldSource | null;
  onSelect: () => void;
}) {
  const chipClass = source
    ? FIELD_SOURCE_STYLES[source].chip
    : "bg-muted text-muted-foreground border border-border";
  return (
    <CommandItem value={`${field.label} ${field.key}`} onSelect={onSelect}>
      <Check
        className={cn("mr-2 h-3 w-3", isSelected ? "opacity-100" : "opacity-0")}
      />
      <div className="flex flex-col gap-0.5">
        <span className="text-xs">{field.label}</span>
        <span
          className={cn(
            "self-start rounded px-1 py-0.5 font-mono text-[9px] font-medium leading-none",
            chipClass,
          )}
        >
          {field.key}
        </span>
      </div>
    </CommandItem>
  );
}

export function CanonicalKeyPicker({
  value,
  onChange,
  className,
  customFields = [],
  contactTags,
  firmContactCustomFields = [],
  appointmentTypes,
  classifySource,
}: CanonicalKeyPickerProps) {
  const [open, setOpen] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<FieldSource | "all">("all");
  // MOR-429: when a template targets specific appointment types, default to
  // hiding fields whose applicability doesn't overlap. The toggle lets the
  // author opt out (advisory filter, not a hard gate).
  const [showAllAppointmentTypes, setShowAllAppointmentTypes] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const resolveSource = useCallback(
    (key: string): FieldSource | null =>
      classifySource ? classifySource(key) : null,
    [classifySource],
  );
  const passesFilter = useCallback(
    (key: string): boolean => {
      if (sourceFilter === "all" || !classifySource) return true;
      return resolveSource(key) === sourceFilter;
    },
    [sourceFilter, classifySource, resolveSource],
  );

  // MOR-429: an appointment-type filter is active when the template carries
  // at least one appointment-type tag AND the author hasn't toggled the
  // "show all" override. The check shows a field if it applies to ANY of
  // the template's tagged appointment types (union semantics).
  const appointmentTypesFiltered =
    !showAllAppointmentTypes && appointmentTypes && appointmentTypes.length > 0
      ? appointmentTypes
      : null;
  const passesAppointmentFilter = useCallback(
    (field: { key: string; appointmentTypes?: AppointmentType[] }): boolean => {
      if (!appointmentTypesFiltered) return true;
      // Fields without an applicability list are universal — always show.
      // Otherwise the field must overlap at least one of the template's
      // tagged appointment types (union semantics, advisory only).
      if (!field.appointmentTypes || field.appointmentTypes.length === 0) {
        return true;
      }
      return field.appointmentTypes.some((t) =>
        appointmentTypesFiltered.includes(t),
      );
    },
    [appointmentTypesFiltered],
  );

  // MOR-563: when the template targets ONLY bankruptcy appointment types,
  // relabel the corporate-flavoured category/field labels ("Company Details",
  // "Director Details", "Director Name", …) to bankruptcy terminology; when
  // it targets ONLY Part X arrangements, to debtor terminology (MOR-1838).
  // A template mixing the two regimes keeps the neutral labels rather than
  // letting whichever tag comes first pick "Bankrupt" or "Debtor". Keys are
  // unchanged; this is display-only.
  const isPartXType = (t: AppointmentType): boolean =>
    isPersonalInsolvencyAppointmentType(t) && !isBankruptcyAppointmentType(t);
  const personalLabelType =
    appointmentTypes &&
    appointmentTypes.length > 0 &&
    (appointmentTypes.every((t) => isBankruptcyAppointmentType(t)) ||
      appointmentTypes.every(isPartXType))
      ? appointmentTypes[0]
      : undefined;

  const dialogContainer =
    triggerRef.current?.closest<HTMLElement>("[role='dialog']");

  const allCanonical = FIELD_CATEGORIES.flatMap((c) => c.fields);

  // Contact-field groups — at most "Job Contact" and "Firm Contact",
  // shown when the template is tagged with that domain's contacts.
  const contactGroups: Array<{
    id: string;
    heading: string;
    fields: Array<{ key: string; label: string }>;
  }> = [];
  if ((contactTags?.jobRoles.length ?? 0) > 0) {
    contactGroups.push({
      id: "job-contact",
      heading: "Job Contact",
      fields: contactFieldPartsForDomain("job").map((p) => ({
        key: buildContactFieldKey({ domain: "job", part: p.part }),
        label: `Job Contact ${p.shortLabel}`,
      })),
    });

    // Add one source group per source kind unlocked by the tagged
    // roles. PDF placements can also bind to source fields, which then
    // hydrate during generation in the same per-source-link fan-out.
    const seenKinds = new Set<ContactSourceKind>();
    const orderedKinds: ContactSourceKind[] = [];
    for (const role of contactTags?.jobRoles ?? []) {
      for (const kind of ROLE_TO_SOURCE_KINDS[role] ?? []) {
        if (seenKinds.has(kind)) continue;
        seenKinds.add(kind);
        orderedKinds.push(kind);
      }
    }
    for (const kind of orderedKinds) {
      const sourceLabel = CONTACT_SOURCE_KIND_LABELS[kind];
      contactGroups.push({
        id: `job-contact-source-${kind}`,
        heading: `Job Contact: ${sourceLabel}`,
        fields: CONTACT_SOURCE_FIELDS[kind].map((meta) => ({
          key: buildContactSourceFieldKey({
            domain: "job",
            sourceKind: kind,
            part: meta.part,
          }),
          label: `Job Contact: ${sourceLabel}: ${meta.label}`,
        })),
      });
    }
  }
  if ((contactTags?.firmCategories.length ?? 0) > 0) {
    const taggedCategories = new Set(contactTags?.firmCategories ?? []);
    const customForTags = firmContactCustomFields.filter((f) =>
      taggedCategories.has(f.category),
    );
    contactGroups.push({
      id: "firm-contact",
      heading: "Firm Contact",
      fields: [
        ...contactFieldPartsForDomain("firm").map((p) => ({
          key: buildContactFieldKey({ domain: "firm", part: p.part }),
          label: `Firm Contact ${p.shortLabel}`,
        })),
        ...customForTags.map((f) => ({
          key: buildContactCustomFieldKey({ domain: "firm", key: f.key }),
          label: `Firm Contact (${FIRM_CATEGORY_META[f.category].label}) — ${f.label}`,
        })),
      ],
    });
  }

  const selectedField =
    allCanonical.find((f) => f.key === value) ??
    customFields.find((f) => f.key === value) ??
    contactGroups.flatMap((g) => g.fields).find((f) => f.key === value);

  const customByCategory = new Map<
    string,
    { label: string; fields: CustomFieldDef[] }
  >();
  for (const cf of customFields) {
    const group = customByCategory.get(cf.categoryId) ?? {
      label: cf.categoryLabel,
      fields: [],
    };
    customByCategory.set(cf.categoryId, group);
    group.fields.push(cf);
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "justify-between overflow-hidden font-mono text-xs",
            className,
          )}
        >
          <span className="truncate">
            {selectedField ? selectedField.label : "Select field..."}
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[36rem] max-w-[calc(100vw-2rem)] p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
        container={dialogContainer}
      >
        <Command onKeyDown={(e) => e.stopPropagation()}>
          {/* Source-filter tabs (when a classifier is wired). */}
          {classifySource && (
            <div className="flex flex-wrap gap-0.5 border-b border-border px-2 py-1.5">
              {[
                { value: "all" as const, label: "All" },
                ...FIELD_SOURCE_ORDER.map((s) => ({
                  value: s,
                  label: FIELD_SOURCE_STYLES[s].label,
                })),
              ].map((opt) => {
                const active = sourceFilter === opt.value;
                const activeChip =
                  opt.value === "all"
                    ? "bg-primary text-primary-foreground"
                    : FIELD_SOURCE_STYLES[opt.value].chip;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSourceFilter(opt.value)}
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[9px] font-medium leading-none transition",
                      active
                        ? activeChip
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
          {/* MOR-429: appointment-type filter toggle — only shown when
              the template has appointment-type tags AND at least one
              field is being hidden by the default filter. */}
          {appointmentTypes && appointmentTypes.length > 0 && (
            <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-1.5 text-[10px] text-muted-foreground">
              <span className="truncate">
                {showAllAppointmentTypes
                  ? "Showing all fields"
                  : `Showing fields for ${appointmentTypes.length === 1 ? appointmentTypes[0].replace(/_/g, " ") : `${appointmentTypes.length} appointment types`}`}
              </span>
              <button
                type="button"
                onClick={() => setShowAllAppointmentTypes((v) => !v)}
                className="shrink-0 rounded px-1.5 py-0.5 font-medium leading-none text-foreground/70 hover:bg-muted hover:text-foreground"
              >
                {showAllAppointmentTypes ? "Filter by type" : "Show all"}
              </button>
            </div>
          )}
          <CommandInput placeholder="Search fields..." />
          <CommandList className="max-h-64 overscroll-contain">
            <CommandEmpty>No matching field.</CommandEmpty>

            {FIELD_CATEGORIES.map((category) => {
              const visible = category.fields.filter(
                (f) => passesFilter(f.key) && passesAppointmentFilter(f),
              );
              if (visible.length === 0) return null;
              return (
                <CommandGroup
                  key={category.id}
                  heading={resolveCategoryLabel(
                    category.id,
                    category.label,
                    personalLabelType,
                  )}
                >
                  {visible.map((field) => {
                    const source = resolveSource(field.key);
                    return (
                      <PickerItem
                        key={field.key}
                        field={{
                          ...field,
                          label: resolveFieldLabel(
                            field.key,
                            field.label,
                            personalLabelType,
                          ),
                        }}
                        isSelected={value === field.key}
                        source={source}
                        onSelect={() => {
                          onChange(field.key);
                          setOpen(false);
                        }}
                      />
                    );
                  })}
                </CommandGroup>
              );
            })}

            {contactGroups.map((group) => {
              const visible = group.fields.filter((f) => passesFilter(f.key));
              if (visible.length === 0) return null;
              return (
                <CommandGroup key={group.id} heading={group.heading}>
                  {visible.map((field) => {
                    const source = resolveSource(field.key);
                    return (
                      <PickerItem
                        key={field.key}
                        field={field}
                        isSelected={value === field.key}
                        source={source}
                        onSelect={() => {
                          onChange(field.key);
                          setOpen(false);
                        }}
                      />
                    );
                  })}
                </CommandGroup>
              );
            })}

            {/* Helper when filtering by a contact source the template
                isn't tagged for — point the user at the template Settings
                to configure roles / firm categories. */}
            {sourceFilter === "job_contact" &&
              !contactGroups.find((g) => g.id === "job-contact") && (
                <CommandGroup heading="Job Contact">
                  <div className="px-2 py-1.5 text-[11px] leading-snug text-muted-foreground">
                    No Job Contact roles are tagged on this template. Add one in
                    the template&apos;s Settings tab to insert Job Contact
                    fields.
                  </div>
                </CommandGroup>
              )}
            {sourceFilter === "firm_contact" &&
              !contactGroups.find((g) => g.id === "firm-contact") && (
                <CommandGroup heading="Firm Contact">
                  <div className="px-2 py-1.5 text-[11px] leading-snug text-muted-foreground">
                    No Firm Contact categories are tagged on this template. Add
                    one in the template&apos;s Settings tab to insert Firm
                    Contact fields.
                  </div>
                </CommandGroup>
              )}

            {[...customByCategory.entries()].map(([catId, group]) => {
              const visible = group.fields.filter((f) => passesFilter(f.key));
              if (visible.length === 0) return null;
              return (
                <CommandGroup
                  key={`custom-${catId}`}
                  heading={`${group.label} (Custom)`}
                >
                  {visible.map((field) => {
                    const source = resolveSource(field.key);
                    return (
                      <PickerItem
                        key={field.key}
                        field={field}
                        isSelected={value === field.key}
                        source={source}
                        onSelect={() => {
                          onChange(field.key);
                          setOpen(false);
                        }}
                      />
                    );
                  })}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
